import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import {
  ApiFilterOperation,
  DictionaryAutocomplete,
  EmptyComponent,
  TableCells,
  TableColumn,
  TableColumnAlign,
  TableFilterCells,
  eqFilterParams,
  useStaticListSource,
} from '@eas/common-web';
import { useCustomFilterAutocomplete } from '@composite/autocomplete-item/autocomplete-item-with-deactivated-items';
import { Redirect } from '@composite/redirect/redirect';
import { StaticDataContext } from '@components/evidence/static-data-provider/static-data-provider';
import {
  autocompleteColumnMapper,
  multipleAutocompleteColumnMapper,
} from '@components/form/misc/label-mappers';
import { CourtFile } from '@models';
import { EssRecordState, EvidenceBrowserUrl, Role } from '@enums';
import { useJudges } from '../../judge/judge-api';
import { useUserRoles } from '../../role/role-api';
import { useCourtFileColumns } from '../court-file-columns';

export const essRecordStates: DictionaryAutocomplete[] = [
  { id: EssRecordState.UNRESOLVED, name: 'Nevyřízen' },
  { id: EssRecordState.RESOLVED, name: 'Vyřízen' },
  { id: EssRecordState.CLOSED, name: 'Uzavřen' },
  { id: EssRecordState.FORWARDED_TO_REGISTRY, name: 'Předán do spisovny' },
  { id: EssRecordState.FORWARDED_TO_ARCHIVE, name: 'Předán do archivu' },
  { id: EssRecordState.CANCELED, name: 'Stornován' },
  { id: EssRecordState.SHREDDED, name: 'Skartován' },
];

export const essRecordStateValueMapper = ({
  value,
}: {
  value?: EssRecordState;
}) => {
  return essRecordStates.find((state) => state.id === value)?.name ?? value;
};

export function useColumns(): TableColumn<CourtFile>[] {
  const intl = useIntl();
  const {
    columnJid,
    columnDeliveryDate,
    columnOpponent,
    columnProposers,
    columnType,
  } = useCourtFileColumns();

  const { states } = useContext(StaticDataContext);
  const useStates = () => useStaticListSource(states);
  const useEssRecordStates = () => useStaticListSource(essRecordStates);

  return [
    {
      datakey: 'id',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_ALL__TABLE__COLUMN__REDIRECT',
        defaultMessage: 'Přesměrování',
      }),
      NameComponent: EmptyComponent,
      width: 50,
      CellComponent: function Cell(props) {
        return (
          <>
            <Redirect
              url={EvidenceBrowserUrl.COURT_FILES_ALL}
              id={props.value}
              showRedirectInCurrent={false}
            />
          </>
        );
      },
      align: TableColumnAlign.LEFT,
    },
    columnDeliveryDate,
    {
      datakey: 'state',
      sortkey: 'state.name',
      filterkey: 'state.id',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_ALL__TABLE__COLUMN__STATE',
        defaultMessage: 'Stav',
      }),
      width: 100,
      CellComponent: TableCells.TextCell,
      FilterComponent: TableFilterCells.useFilterSelectCellFactory(useStates),
      valueMapper: TableCells.useSelectCellFactory(useStates),
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'number.value',
      sortkey: 'number.valueSort',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_ALL__TABLE__COLUMN__FILE_NUMBER',
        defaultMessage: 'Spisová značka',
      }),
      width: 120,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'incomingNumber',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_ALL__TABLE__COLUMN__INCOMING_NUMBER',
        defaultMessage: 'Došlé číslo',
      }),
      width: 100,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    columnJid,
    { ...columnProposers, filterDialogSpan: 2 },
    columnType,
    { ...columnOpponent, filterDialogSpan: 2 },
    ...useAdditionalCourtColumns(),
    {
      datakey: 'number.year',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_ALL__TABLE__COLUMN__NUMBER_YEAR',
        defaultMessage: 'Rok',
      }),
      width: 100,
      CellComponent: TableCells.NumberCell,
      FilterComponent: TableFilterCells.FilterNumberCell,
      sortable: true,
      filterable: true,
      visible: false,
    },
    {
      datakey: 'essRecord.state',
      filterkey: 'essRecord.state.id',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_ALL__TABLE__COLUMN__ESS_RECORD_STATE',
        defaultMessage: 'Stav záznamu v spisovej službe',
      }),
      width: 100,
      CellComponent: TableCells.TextCell,
      FilterComponent:
        TableFilterCells.useFilterMultipleSelectCellFactory(useEssRecordStates),
      valueMapper: essRecordStateValueMapper,
      sortable: false,
      filterable: true,
      visible: false,
    },
  ];
}
// https://inqool.atlassian.net/browse/USCR-2732
function useAdditionalCourtColumns(): TableColumn<CourtFile>[] {
  const intl = useIntl();

  const useSecretaries = () =>
    useUserRoles({
      ...eqFilterParams({ field: 'role.id', value: Role.SECRETARY }),
      include: ['DEACTIVATED', 'DELETED'],
    });
  const useAssistants = () =>
    useUserRoles({
      ...eqFilterParams({ field: 'role.id', value: Role.ASSISTANT }),
      include: ['DEACTIVATED', 'DELETED'],
    });

  const useIndexes = () =>
    useUserRoles({
      ...eqFilterParams({ field: 'role.id', value: Role.INDEX }),
      include: ['DEACTIVATED'],
    });

  const useAnalysts = () =>
    useUserRoles({
      ...eqFilterParams({ field: 'role.id', value: Role.ANALYST }),
      include: ['DEACTIVATED'],
    });

  return [
    {
      datakey: 'judge',
      filterkey: 'judge.id',
      sortkey: 'judge.name',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_JUDGE_OFFICE__TABLE__COLUMN__JUDGE',
        defaultMessage: 'Soudce zpravodaj',
      }),
      width: 200,
      valueMapper: autocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: () => useJudges({ include: ['DEACTIVATED'] }),
      }),
      filterOperation: ApiFilterOperation.EQ,
      filterDialogSpan: 2,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'secretaryUserRole',
      filterkey: 'secretaryUserRole.id',
      sortkey: 'secretaryUserRole.name',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_JUDGE_OFFICE__TABLE__COLUMN__SECRETARY',
        defaultMessage: 'Tajemnice',
      }),
      width: 200,
      valueMapper: autocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: useSecretaries,
      }),
      filterOperation: ApiFilterOperation.EQ,
      filterDialogSpan: 2,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'assistantUserRoles',
      filterkey: 'assistantUserRoles.id',
      sortkey: 'assistantUserRoles.flat',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_JUDGE_OFFICE__TABLE__COLUMN__ASSISTANTS',
        defaultMessage: 'Asistent',
      }),
      width: 200,
      valueMapper: multipleAutocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: useAssistants,
      }),
      filterOperation: ApiFilterOperation.EQ,
      filterDialogSpan: 2,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'indexUserRole',
      filterkey: 'indexUserRole.id',
      sortkey: 'indexUserRole.name',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_JUDGE_OFFICE__TABLE__COLUMN__INDEX',
        defaultMessage: 'Vedoucí rejstříku',
      }),
      width: 200,
      valueMapper: autocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: useIndexes,
      }),
      filterOperation: ApiFilterOperation.EQ,
      filterDialogSpan: 2,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'analystUserRole',
      filterkey: 'analystUserRole.id',
      sortkey: 'analystUserRole.name',
      name: intl.formatMessage({
        id: 'ES__ES_FILES__TABLE__COLUMN__ANALYST',
        defaultMessage: 'Analytik - indexace',
      }),
      width: 200,
      valueMapper: autocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: useAnalysts,
      }),
      filterOperation: ApiFilterOperation.EQ,
      filterDialogSpan: 2,
      filterable: true,
      sortable: true,
    },
  ];
}
